import type { ComponentProps } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";

const badgeVariants = cva(
  "inline-flex h-5 items-center gap-1.5 px-1.5 text-2xs font-medium uppercase tracking-wider whitespace-nowrap",
  {
    variants: {
      tone: {
        neutral: "bg-surface-2 text-ink-2",
        success: "bg-success-soft text-success",
        warning: "bg-warning-soft text-warning",
        danger: "bg-danger-soft text-danger",
        info: "bg-info-soft text-info",
        brand: "bg-brand-soft text-brand-ink",
      },
    },
    defaultVariants: { tone: "neutral" },
  },
);

export type BadgeProps = ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & {
    /** Leading status dot in the tone color. */
    dot?: boolean;
  };

export type BadgeTone = NonNullable<BadgeProps["tone"]>;

/** The tones a status can take: every tone but brand, which marks Ghezaa's own things, never a state. */
export type StatusTone = Exclude<BadgeTone, "brand">;

/**
 * One colour per status word, in every app: the colour says what the status asks of the person reading it.
 * success — done, and done well. info — moving: someone is on it. warning — waiting on someone here.
 * danger — went wrong. neutral — nothing to do: not started, settled or ended.
 * A word means the same everywhere (Delivered is green on an order, a release and a delivery); a status shown to a
 * person takes its tone from here through `statusTone`, never from a tone written in a page. A new word is added here.
 */
export const statusTones = {
  // done, and done well
  confirmed: "success",
  accepted: "success",
  acceptedInPart: "success",
  delivered: "success",
  paid: "success",
  resolved: "success",
  read: "success",
  ended: "success",
  sent: "success",
  active: "success",
  matched: "success",
  // moving: someone is on it
  open: "info",
  released: "info",
  inFulfilment: "info",
  preparing: "info",
  picking: "info",
  shipped: "info",
  outForDelivery: "info",
  partPaid: "info",
  running: "info",
  rendering: "info",
  invited: "info",
  // waiting on someone here
  new: "warning",
  toDecide: "warning",
  pending: "warning",
  awaitingConfirmation: "warning",
  onHold: "warning",
  needsReview: "warning",
  noteToCheck: "warning",
  signedNoteMissing: "warning",
  waitingForYou: "warning",
  delayed: "warning",
  locked: "warning",
  /** A customer that has stopped buying for more than twice its usual gap (Sales): its salesperson should call. */
  silent: "warning",
  quantitiesDiffer: "warning",
  noNote: "warning",
  dateUnreadable: "warning",
  // went wrong
  rejected: "danger",
  notAccepted: "danger",
  notDelivered: "danger",
  overdue: "danger",
  failed: "danger",
  exception: "danger",
  missedRelease: "danger",
  unexpectedNote: "danger",
  /** A BC customer blocked for shipping, invoicing or everything (Sales). */
  blocked: "danger",
  // nothing to do: not started, settled or ended
  noDelivery: "neutral",
  planned: "neutral",
  received: "neutral",
  submitted: "neutral",
  queued: "neutral",
  waiting: "neutral",
  closed: "neutral",
  cancelled: "neutral",
  voided: "neutral",
  credited: "neutral",
  disabled: "neutral",
  notSent: "neutral",
} as const satisfies Record<string, StatusTone>;

export type StatusWord = keyof typeof statusTones;

/** The tone of a status word (see `statusTones`). */
export function statusTone(word: StatusWord): StatusTone {
  return statusTones[word];
}

export function Badge({ className, tone, dot = false, children, ...props }: BadgeProps) {
  return (
    <span data-slot="badge" data-tone={tone ?? "neutral"} className={cn(badgeVariants({ tone }), className)} {...props}>
      {dot && <span aria-hidden className="size-1.5 rounded-full bg-current" />}
      {children}
    </span>
  );
}
